// ============================================================================
// Margin Distribution Widget — histogram of site-level margin % across the
// active portfolio. 20 bins of 2.5pp width from 0% to 50%.
//
// Color bands (from dashboard README v2, 2026-05-12):
//   * <15%   → red    — flag for review, possible under-pricing or labor blowout
//   * 15-25% → blue   — transition zone
//   * 25-40% → green  — healthy range (26.5% is the floor per pricing model)
//   * >=40%  → yellow — flag for review, possible mispriced legacy or atypical labor
//
// On hover/tap: tooltip-style listing of the first 10 facilities in that bin
// + their margin %. Helps Bryan answer "which sites are in the red zone?"
// without a separate drill.
// ============================================================================

import { useState } from 'react';
import type { MarginBucket } from './use-dashboard-data';
import { WidgetCard, WidgetSkeleton } from './widget-card';

interface Props {
  data: MarginBucket[] | null;
  loading: boolean;
}

export function MarginDistributionWidget({ data, loading }: Props) {
  const [activeBin, setActiveBin] = useState<number | null>(null);

  if (loading && !data) {
    return (
      <WidgetCard title="Margin distribution" minHeight={320}>
        <WidgetSkeleton lines={8} />
      </WidgetCard>
    );
  }

  if (!data || data.length === 0) {
    return (
      <WidgetCard title="Margin distribution" minHeight={320}>
        <p className="text-sm text-tcs-text-muted">
          No margin data available yet.
        </p>
      </WidgetCard>
    );
  }

  const total = data.reduce((s, b) => s + b.count, 0);
  const maxCount = Math.max(1, ...data.map((b) => b.count));
  const H = 180;
  const W = 500;
  const PAD = { l: 28, r: 8, t: 8, b: 28 };
  const chartW = W - PAD.l - PAD.r;
  const chartH = H - PAD.t - PAD.b;
  const barW = chartW / data.length;

  return (
    <WidgetCard
      title="Margin distribution"
      subtitle={`${total} site${total === 1 ? '' : 's'} with margin observable · floor 26.5%`}
      minHeight={320}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        className="w-full"
        style={{ height: H }}
        role="img"
        aria-label="Margin distribution histogram"
      >
        {/* Y axis grid */}
        {[0, 0.25, 0.5, 0.75, 1].map((p) => {
          const y = PAD.t + chartH * (1 - p);
          const val = Math.round(maxCount * p);
          return (
            <g key={p}>
              <line
                x1={PAD.l}
                y1={y}
                x2={W - PAD.r}
                y2={y}
                stroke="var(--color-tcs-border, #E5E7EB)"
                strokeWidth={1}
              />
              <text
                x={PAD.l - 4}
                y={y + 3}
                textAnchor="end"
                fontSize={9}
                fill="var(--color-tcs-text-muted, #6B7280)"
              >
                {val}
              </text>
            </g>
          );
        })}
        {/* Bars */}
        {data.map((b, i) => {
          if (b.count === 0) return null;
          const h = (b.count / maxCount) * chartH;
          const x = PAD.l + i * barW;
          const y = PAD.t + chartH - h;
          const isActive = activeBin === i;
          return (
            <rect
              key={i}
              x={x + 1}
              y={y}
              width={Math.max(1, barW - 2)}
              height={h}
              fill={colorForBin(b.bin_lo)}
              opacity={isActive ? 1 : 0.85}
              style={{ cursor: 'pointer' }}
              onMouseEnter={() => setActiveBin(i)}
              onMouseLeave={() => setActiveBin(null)}
            />
          );
        })}
        {/* X axis labels */}
        {[0, 0.1, 0.2, 0.3, 0.4, 0.5].map((p) => {
          const x = PAD.l + (p / 0.5) * chartW;
          return (
            <text
              key={p}
              x={x}
              y={H - 12}
              textAnchor="middle"
              fontSize={10}
              fill="var(--color-tcs-text-muted, #6B7280)"
            >
              {Math.round(p * 100)}%
            </text>
          );
        })}
        {/* 26.5% floor reference line */}
        <line
          x1={PAD.l + (0.265 / 0.5) * chartW}
          y1={PAD.t}
          x2={PAD.l + (0.265 / 0.5) * chartW}
          y2={PAD.t + chartH}
          stroke="var(--color-tcs-text, #111827)"
          strokeDasharray="3 3"
          strokeWidth={1}
        />
        <text
          x={PAD.l + (0.265 / 0.5) * chartW}
          y={PAD.t + 10}
          textAnchor="middle"
          fontSize={9}
          fill="var(--color-tcs-text, #111827)"
        >
          26.5% floor
        </text>
      </svg>

      {/* Legend */}
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-tcs-text-muted">
        <LegendDot color={colorForBin(0)} label="<15% review" />
        <LegendDot color={colorForBin(0.15)} label="15–25%" />
        <LegendDot color={colorForBin(0.25)} label="25–40% healthy" />
        <LegendDot color={colorForBin(0.4)} label="≥40% review" />
      </div>

      {/* Hover detail panel */}
      {activeBin !== null && data[activeBin].count > 0 && (
        <div className="mt-3 rounded border border-tcs-border bg-tcs-bg-muted p-2 text-xs">
          <div className="font-medium text-tcs-text">
            {(data[activeBin].bin_lo * 100).toFixed(1)}% –{' '}
            {(data[activeBin].bin_hi * 100).toFixed(1)}% · {data[activeBin].count}{' '}
            {data[activeBin].count === 1 ? 'site' : 'sites'}
          </div>
          <ul className="mt-1 space-y-0.5 text-tcs-text-muted">
            {data[activeBin].examples.map((ex) => (
              <li key={ex.facility_id} className="truncate">
                {ex.facility_name}
                <span className="ml-2 tabular-nums">
                  · {(ex.margin * 100).toFixed(1)}%
                </span>
              </li>
            ))}
            {data[activeBin].count > data[activeBin].examples.length && (
              <li className="text-tcs-text-muted">
                +{data[activeBin].count - data[activeBin].examples.length} more
              </li>
            )}
          </ul>
        </div>
      )}
    </WidgetCard>
  );
}

function colorForBin(bin_lo: number): string {
// TODO(cw-j phase 1c): these reuse the RAG hues for a NON-RAG semantic,
// which the map palette rule forbids (green/amber/red mean status only).
// They are still the OLD literals, so they no longer match the RAG tokens.
// Decide in 1c: map onto the status tokens, or move to a neutral series
// palette. Left as literals deliberately — changing them is visible.
  if (bin_lo < 0.15) return '#EF4444'; // red
  if (bin_lo < 0.25) return '#5B8DB8'; // tcs blue
  if (bin_lo < 0.4) return '#10B981'; // green
  return '#F59E0B'; // yellow / amber
}

function LegendDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block h-2 w-2 rounded-sm"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}
